import React, { useEffect, useMemo, useState } from 'react';
import { STATS_REFRESH_MS, usePriceStatsQuery } from '../api/stats.queries';

export default function PriceCoverageSummary({ compact = false }) {
  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = usePriceStatsQuery();
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const upToDate = data?.symbols_price_up_to_date ?? 0;
  const missing = data?.symbols_price_missing ?? 0;
  const unsupported = data?.symbols_price_unsupported ?? 0;
  const limitedUpToDate = data?.symbols_price_up_to_date_limited ?? 0;
  const fundamentalsUpToDate = data?.symbols_fundamentals_up_to_date ?? 0;
  const fundamentalsMissing = data?.symbols_fundamentals_missing ?? 0;
  const fundamentalsUnsupported = data?.symbols_fundamentals_unsupported ?? 0;
  const coveragePct = data?.coverage_full_price_360_pct ?? null;
  const totalSymbols = data?.total_symbols ?? null;

  const primaryStats = [
    { key: 'price-fresh', label: 'Price fresh', value: upToDate, accent: 'ok' },
    { key: 'price-missing', label: 'Price missing', value: missing, accent: missing ? 'warn' : '' },
    { key: 'fund-fresh', label: 'Fundamentals fresh', value: fundamentalsUpToDate, accent: 'ok' },
    {
      key: 'fund-missing',
      label: 'Fundamentals missing',
      value: fundamentalsMissing,
      accent: fundamentalsMissing ? 'warn' : '',
    },
  ];

  const secondaryStats = compact
    ? []
    : [
        {
          key: 'price-limited',
          label: 'Limited span',
          value: limitedUpToDate,
          accent: limitedUpToDate ? 'muted' : '',
        },
        {
          key: 'price-unsupported',
          label: 'Price unsupported',
          value: unsupported,
          accent: unsupported ? 'muted' : '',
        },
        {
          key: 'fund-unsupported',
          label: 'Fundamentals unsupported',
          value: fundamentalsUnsupported,
          accent: fundamentalsUnsupported ? 'muted' : '',
        },
      ];

  const stats = [...primaryStats, ...secondaryStats].map((stat) => ({
    ...stat,
    displayValue: stat.value.toLocaleString(),
  }));
  const nextRefreshInSeconds = useMemo(() => {
    if (!dataUpdatedAt || isLoading) return null;
    if (isFetching) return 0;
    const elapsedMs = Math.max(0, nowMs - dataUpdatedAt);
    const remainingMs = Math.max(0, STATS_REFRESH_MS - elapsedMs);
    return Math.ceil(remainingMs / 1000);
  }, [dataUpdatedAt, isFetching, isLoading, nowMs]);
  const refreshHint = isFetching
    ? 'Refreshing now…'
    : nextRefreshInSeconds === null
      ? 'Waiting for first sync…'
      : `Next refresh in ${nextRefreshInSeconds}s`;

  return (
    <section className={['card', compact ? 'card-compact' : ''].filter(Boolean).join(' ')}>
      <header className="card-header">
        <h2 className="card-title">Coverage</h2>
      </header>

      {isLoading ? (
        <p className="subtle">Loading price stats…</p>
      ) : isError ? (
        <div className="error-box">
          <p>Unable to load stats.</p>
          <p className="subtle" style={{ marginTop: '.35rem' }}>{String(error?.message || error)}</p>
        </div>
      ) : (
        <>
          <div className={['coverage-stats', compact ? 'coverage-stats--compact' : ''].filter(Boolean).join(' ')}>
            {stats.map(({ key, label, displayValue, accent }) => (
              <div key={key} className="coverage-stat">
                <span className={['coverage-value', accent].filter(Boolean).join(' ')}>{displayValue}</span>
                <span className="coverage-label">{label}</span>
              </div>
            ))}
          </div>
          {totalSymbols !== null || coveragePct !== null ? (
            <p className="subtle" style={{ marginTop: '.35rem' }}>
              {totalSymbols !== null ? `${totalSymbols.toLocaleString()} symbols` : null}
              {coveragePct !== null ? ` • ${coveragePct}% meet the 360-day window` : null}
            </p>
          ) : null}
        </>
      )}
      <footer className="coverage-footer">
        <span className="coverage-refresh-hint">{refreshHint}</span>
        <button className="ghost sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </footer>
    </section>
  );
}
